// eslint-disable-next-line no-restricted-imports
import { LegalClientFragment } from 'api/generated/graphql';
import {
  ClientClassificationType,
  ClientTypeClient,
  LegalClientEntity,
  TariffType,
} from 'api/types/entity';
import { lodash } from 'helpers';

import { parseFileDocumentEntity } from '../client/parseEntity/parseFileEntity';
import { parseInformationUpdateFrequency } from '../client/parseEntity/parseInformationUpdateFrequency';
import { parseRiskLevel } from '../client/parseEntity/parseRiskLevel';
import { parseWithdrawalAccountsEntity } from '../client/parseEntity/parseWithdrawalAccountsEntity';
import { parseW8BENFormEntity } from '../parseEntity';
import { parseManagementType } from '../parseEntity/parseManagementType';
import { parsePersonalAccountEntity } from '../parseEntity/parsePersonalAccountEntity';
import { parseInvoiceEntity } from '../parseInvoiceEntity';
import { parseManagerData } from '../parseManagerData';
import { parseOboardingData } from '../parseOboardingData';

import { parseLegalClientBeneficiaryItemEntity } from './parseEntity/innerItemEntity/parseLegalClientBeneficiaryItemEntity';
import { parseLegalClientBranchItemEntity } from './parseEntity/innerItemEntity/parseLegalClientBranchItemEntity';
import { parseLegalClientHeadItemEntity } from './parseEntity/innerItemEntity/parseLegalClientHeadItemEntity';
import { parseLegalClientLegalHeadItemEntity } from './parseEntity/innerItemEntity/parseLegalClientLegalHeadItemEntity';
import { parseBusinessRelationshipEntity } from './parseEntity/parseBusinessRelationshipEntity';
import { parseCategoryType } from './parseEntity/parseCategoryType';
import { parseLegalClientConstitutionalDocument } from './parseEntity/parseConstitutionalDocument';
import { parseDebtsEntity } from './parseEntity/parseDebtsEntity';
import { parseEconomicSectorType } from './parseEntity/parseEconomicSectorType';
import { parseFinancialMonitoringEntity } from './parseEntity/parseFinancialMonitoringEntity';
import { parseLegalClientAccountStatus } from './parseEntity/parseLegalClientAccountStatus';
import { parseLegalClientActivityTypes } from './parseEntity/parseLegalClientActivityTypes';
import { parseLegalClientBankAccounts } from './parseEntity/parseLegalClientBankAccounts';
import { parseLegalClientBodyEntity } from './parseEntity/parseLegalClientBodyEntity';
import { parseLegalClientFinancingSourcesEntity } from './parseEntity/parseLegalClientFinancingSourcesEntity';
import { parseLegalClientInvestmentPurposeAndNatureEntity } from './parseEntity/parseLegalClientInvestmentPurposeAndNatureEntity';
import { parseLegalClientKnowledgeAndExperienceEntity } from './parseEntity/parseLegalClientKnowledgeAndExperienceEntity';
import { parseLegalClientLegalAddressEntity } from './parseEntity/parseLegalClientLegalAddressEntity';
import { parseLegalClientLicenses } from './parseEntity/parseLegalClientLicenses';
import { parseLegalClientOnboardingStatus } from './parseEntity/parseLegalClientOnboardingStatus';
import { parseLegalClientOpenAccountOrderEntity } from './parseEntity/parseLegalClientOpenAccountOrderEntity';
import { parseLegalClientOrderSubmissionContactsEntity } from './parseEntity/parseLegalClientOrderSubmissionContactsEntity';
import { parseLegalClientSaveActivityEntity } from './parseEntity/parseLegalClientSaveActivityEntity';
import { parseLegalClientServiceContractEntity } from './parseEntity/parseLegalClientServiceContractEntity';
import { parseLegalClientSourcesOfIncomeEntity } from './parseEntity/parseLegalClientSourcesOfIncomeEntity';
import { parseLegalClientStateRegistration } from './parseEntity/parseLegalClientStateRegistration';
import { parseLegalClientTargetedFinancialInstrumentsEntity } from './parseEntity/parseLegalClientTargetedFinancialInstrumentsEntity';
import { parseLegalClientTaxResidency } from './parseEntity/parseLegalClientTaxResidency';
import { parseOrganizationalAndLegalFormEntity } from './parseEntity/parseOrganizationalAndLegalFormEntity';
import { parseRegistrationPlaceEntity } from './parseEntity/parseRegistrationPlaceEntity';
import { parseLegalClientOboardingData } from './parseLegalClientOnboardingData';
import { parseLegalClientRepresentativeClientInfoEntity } from './parseLegalClientRepresentativeClientInfoEntity';

export const parseLegalClient = (
  client: LegalClientFragment
): LegalClientEntity => ({
  id: client.id,
  brightCode: client.brightCode ?? undefined,
  versionNumber: client.version?.number,
  versionId: client.version?.id,
  createdAt: new Date(client.createdAt),
  representativeId: client.representative?.id,
  representative: client.representative
    ? parseLegalClientRepresentativeClientInfoEntity(client.representative)
    : undefined,
  fullName: client.fullName ?? undefined,
  shortName: client.shortName ?? undefined,
  fullNameEng: client.fullNameEng ?? undefined,
  shortNameEng: client.shortNameEng ?? undefined,
  registrationPlace: client.registrationPlace
    ? parseRegistrationPlaceEntity(client.registrationPlace)
    : undefined,
  legalAddress: parseLegalClientLegalAddressEntity(client.legalAddress),
  actualAddress: client.actualAddress,
  activityTypes: parseLegalClientActivityTypes(client.activityTypes),
  OKPOCode: client.OKPOCode ?? undefined,
  organizationalAndLegalForm: parseOrganizationalAndLegalFormEntity(
    client.organizationalAndLegalForm ?? undefined
  ),
  taxResidences: client.taxResidences
    ? lodash.compact(client.taxResidences).map(parseLegalClientTaxResidency)
    : undefined,
  bankAccounts: parseLegalClientBankAccounts(client.bankAccounts),
  contacts: client.contacts,
  stateRegistration: parseLegalClientStateRegistration(client),
  debts: parseDebtsEntity(client.debts),
  licenses: parseLegalClientLicenses(client.licenses),
  isLicensed: client.isLicensed ?? undefined,
  ownershipStructureFiles: client.ownershipStructureDocuments
    ? lodash
        .compact(client.ownershipStructureDocuments)
        .map(parseFileDocumentEntity)
    : undefined,
  bodies: client.bodies
    ? lodash.compact(client.bodies).map(parseLegalClientBodyEntity)
    : undefined,
  constitutionalDocument: parseLegalClientConstitutionalDocument(
    client.constitutionalDocument
  ),
  beneficiaries: client.beneficiaries
    ? lodash
        .compact(client.beneficiaries)
        .map(parseLegalClientBeneficiaryItemEntity)
    : undefined,
  heads: client.heads
    ? lodash.compact(client.heads).map(parseLegalClientHeadItemEntity)
    : undefined,
  legalHeads: client.legalHeads
    ? lodash.compact(client.legalHeads).map(parseLegalClientLegalHeadItemEntity)
    : undefined,
  tariff: client.tariff ? TariffType[client.tariff] : undefined,
  financingSource: parseLegalClientFinancingSourcesEntity(
    client.financingSources
  ),
  financialMonitoring: parseFinancialMonitoringEntity(
    client.financialMonitoring
  ),

  businessRelationship: parseBusinessRelationshipEntity(
    client.businessRelationship
  ),
  withdrawalAccounts: parseWithdrawalAccountsEntity(client.withdrawalAccounts),
  orderSubmissionContacts: parseLegalClientOrderSubmissionContactsEntity(
    client.orderSubmissionContacts
  ),
  branches: client.branches
    ? lodash.compact(client.branches).map(parseLegalClientBranchItemEntity)
    : undefined,
  onboardingStatus: parseLegalClientOnboardingStatus(client.onboardingStatus),
  accountStatus: parseLegalClientAccountStatus(client.accountStatus),

  code: client.code ?? undefined,
  centralDepositoryAccountNumber:
    client.centralDepositoryAccountNumber ?? undefined,

  centralDepositoryAccountNumberOpeningDate:
    client.centralDepositoryAccountNumberOpeningDate
      ? new Date(client.centralDepositoryAccountNumberOpeningDate)
      : undefined,

  personalAccount: parsePersonalAccountEntity(client.personalAccount),

  manager: client.manager ? parseManagerData(client.manager) : undefined,

  riskLevel: client.riskLevel ? parseRiskLevel(client.riskLevel) : undefined,

  accreditedInvestorStatus: client.accreditedInvestorStatus ?? undefined,

  informationUpdateFrequency: client.informationUpdateFrequency
    ? parseInformationUpdateFrequency(client.informationUpdateFrequency)
    : undefined,

  signaturesCardFiles: client.signaturesCardDocuments
    ? lodash
        .compact(client.signaturesCardDocuments)
        .map(parseFileDocumentEntity)
    : undefined,

  openAccountOrder_Finance: parseLegalClientOpenAccountOrderEntity(
    client.openAccountOrder_Finance
  ),

  additionalDocumentFiles: lodash
    .compact(client.additionalDocuments)
    .map(parseFileDocumentEntity),
  additionalInformationFiles: lodash
    .compact(client.additionalInformationDocuments)
    .map(parseFileDocumentEntity),

  unidentifiableBeneficiaryLetterFiles: lodash
    .compact(client.unidentifiableBeneficiaryLetterDocuments)
    .map(parseFileDocumentEntity),

  questionnaireFile: client.questionnaireDocument
    ? parseFileDocumentEntity(client.questionnaireDocument)
    : undefined,

  signedQuestionnaireFiles: lodash
    .compact(client.signedQuestionnaireDocuments)
    .map(parseFileDocumentEntity),

  invoice: parseInvoiceEntity(client.invoice),

  category: parseCategoryType(client.category),
  economicSector: parseEconomicSectorType(client.economicSector),
  managementType: parseManagementType(client.managementType),
  clientType: client.clientType
    ? ClientTypeClient[client.clientType]
    : undefined,
  classification: client.classification
    ? ClientClassificationType[client.classification]
    : undefined,
  isInsider: client.isInsider ?? undefined,

  isFinancialMonitoringSubject:
    client.isFinancialMonitoringSubject ?? undefined,
  isKazakhstanResident: client.isKazakhstanResident ?? undefined,
  taxBenefits: client.taxBenefits ?? undefined,

  serviceContract: parseLegalClientServiceContractEntity(
    client.serviceContract
  ),

  riskLevelReviewDate: client.riskLevelReviewDate
    ? new Date(client.riskLevelReviewDate)
    : undefined,
  lastInformationUpdateDate: client.lastInformationUpdateDate
    ? new Date(client.lastInformationUpdateDate)
    : undefined,
  lastOperationMonitoringResults:
    client.lastOperationMonitoringResults ?? undefined,
  usedServices: client.usedServices ?? undefined,

  targetedFinancialInstruments: client.targetedFinancialInstruments
    ? parseLegalClientTargetedFinancialInstrumentsEntity(
        client.targetedFinancialInstruments
      )
    : undefined,

  sourcesOfIncome: client.sourcesOfIncome
    ? parseLegalClientSourcesOfIncomeEntity(client.sourcesOfIncome)
    : undefined,

  identificationNumbers: client.identificationNumbers ?? undefined,

  representativeOnboarding: parseOboardingData(
    client.representative?.onboarding
  ),

  onboarding: parseLegalClientOboardingData(client.onboarding),

  activity: parseLegalClientSaveActivityEntity(client.activity ?? undefined),

  knowledgeAndExperience: client.knowledgeAndExperience
    ? parseLegalClientKnowledgeAndExperienceEntity(
        client.knowledgeAndExperience
      )
    : undefined,

  investmentPurposeAndNature: client.investmentPurposeAndNature
    ? parseLegalClientInvestmentPurposeAndNatureEntity(
        client.investmentPurposeAndNature
      )
    : undefined,

  W8BENForm: client.W8BENForm
    ? parseW8BENFormEntity(client.W8BENForm)
    : undefined,

  clientServiceAgreementFiles_Global:
    client.clientServiceAgreementDocuments_Global
      ? {
          blank: client.clientServiceAgreementDocuments_Global.blank
            ? parseFileDocumentEntity(
                client.clientServiceAgreementDocuments_Global.blank
              )
            : undefined,
          companySigned: client.clientServiceAgreementDocuments_Global
            .companySigned
            ? parseFileDocumentEntity(
                client.clientServiceAgreementDocuments_Global.companySigned
              )
            : undefined,
          fullySigned: client.clientServiceAgreementDocuments_Global.fullySigned
            ? parseFileDocumentEntity(
                client.clientServiceAgreementDocuments_Global.fullySigned
              )
            : undefined,
        }
      : undefined,

  companySignedNoticeOfInvestmentAccountOpeningFile:
    client.companySignedNoticeOfInvestmentAccountOpeningDocument
      ? parseFileDocumentEntity(
          client.companySignedNoticeOfInvestmentAccountOpeningDocument
        )
      : undefined,

  isBrightSyncEnabled: client.isBrightSyncEnabled,
});
